/**
 * Draggable Tab Component
 *
 * A tab item that can be dragged and dropped for reordering.
 * Uses @dnd-kit for drag-and-drop functionality.
 */

import { useTabsContext } from '@/context/TabsContext';
import type { TabItem } from '@/context/TabsContext/types';
import { getTabDisplayTitle } from '@/utils/routeUtils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CloseOutlined, PushpinFilled } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React from 'react';
import { TabContextMenu } from './TabContextMenu';

type DraggableTabProps = {
  tab: TabItem;
  isActive: boolean;
  onFocus: () => void;
  onClose: () => void;
};

export const DraggableTab: React.FC<DraggableTabProps> = ({
  tab,
  isActive,
  onFocus,
  onClose,
}) => {
  const { config } = useTabsContext();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: tab.key,
    disabled: !config.enableDragReorder,
  });
  const { role: _role, tabIndex: _tabIndex, ...sortableAttributes } = attributes;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : isActive ? 1 : 0,
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger click if we're dragging
    if (isDragging) return;
    e.preventDefault();
    onFocus();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Middle click to close
    if (e.button === 1 && !tab.pinned) {
      e.preventDefault();
      onClose();
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const displayTitle = getTabDisplayTitle(tab);

  const tabClassName = [
    'tabs-layout-tab',
    isActive && 'tabs-layout-tab-active',
    tab.pinned && 'tabs-layout-tab-pinned',
    isDragging && 'tabs-layout-tab-dragging',
    isOver && 'tabs-layout-tab-drop-target',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TabContextMenu tab={tab}>
      <div
        ref={setNodeRef}
        className={tabClassName}
        style={style}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        role="tab"
        aria-selected={isActive}
        tabIndex={0}
        data-testid="tabs-layout-tab"
        data-tab-key={tab.key}
        data-tab-path={tab.path}
        data-tab-title={displayTitle}
        {...sortableAttributes}
        {...listeners}
      >
        {tab.pinned && <PushpinFilled className="tabs-layout-tab-pin-icon" />}
        <Tooltip title={displayTitle} mouseEnterDelay={0.5}>
          <span className="tabs-layout-tab-title">{displayTitle}</span>
        </Tooltip>
        {!tab.pinned && (
          <button
            className="tabs-layout-tab-close"
            onClick={handleCloseClick}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={`Close ${displayTitle}`}
            type="button"
          >
            <CloseOutlined />
          </button>
        )}
      </div>
    </TabContextMenu>
  );
};

export default DraggableTab;
